/** @jsxImportSource @compiled/react */
/* eslint-disable react/no-unknown-property */
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { colors } from '@atlaskit/theme';
import { css } from '@compiled/react';
import messages from './PasswordStrengthMeter.messages';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePasswordScore, atlassianKeywords } from './usePasswordScore';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { Stack, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  getMinimumPasswordLength,
  minimumPasswordLengthMessage,
} from '../../utilities/password-policy';

const barWidths = ['0%', '20%', '40%', '60%', '80%', '100%'];

type BarProps = {
  score: number | null;
};

const Wrapper = styled.div`
  margin-bottom: ${token('space.100')};
`;

const textStyles = css({
  textAlign: 'center',
  '& *': {
    fontFamily: token('font.family.brand.body'),
  },
});

export const passwordStrengthMessageId = 'password-strength-meter';

const Bar = styled.div<BarProps>`
  background: ${token('color.border')};
  height: 2px;
  margin-top: 5px;
  position: relative;

  &::before {
    background: ${props =>
      props.score === null ? token('color.border') : barColours[props.score]};
    bottom: 0;
    content: '';
    left: 0;
    position: absolute;
    top: 0;
    transition: all 0.2s ease-out;
    width: ${props => (props.score === null ? 0 : barWidths[props.score])};
  }
`;

type PasswordStrengthMeterContainerProps = BarProps & {
  title: string;
};

const barColours = [
  token('color.border'),
  colors.R500,
  colors.R300,
  colors.Y300,
  colors.G300,
  colors.G400,
];

const Container = (props: PasswordStrengthMeterContainerProps) => (
  <Wrapper>
    <Stack>
      <Bar score={props.score}>
        <Dividers />
      </Bar>
      <div role="status">
        {props.title && (
          <Stack>
            <VisuallyHidden>
              <FormattedMessage
                id="password.strength.accessibility.hint"
                defaultMessage="Password strength: "
                description="Text that precedes the password strength hint. Hidden except for screen reader users."
              />
            </VisuallyHidden>
            <span css={textStyles} id={passwordStrengthMessageId}>
              <Text size="small" color="color.text.subtlest" align="center">
                {props.title}
              </Text>
            </span>
          </Stack>
        )}
      </div>
    </Stack>
  </Wrapper>
);

const Dividers = styled.span`
  border-left: 5px solid #fff;
  border-right: 5px solid #fff;
  bottom: 0;
  left: 19%;
  position: absolute;
  right: 19%;
  top: 0;

  &::after {
    border-left: 5px solid #fff;
    border-right: 5px solid #fff;
    bottom: 0;
    content: '';
    left: 30%;
    position: absolute;
    right: 30%;
    top: 0;
  }
`;

export type PasswordStrengthMeterProps = {
  i18nLevels?: string[];
  i18nNoValue?: string;
  i18nNoZxcvbn?: string;
  password: string;
  email: string;
  displayName: string;
};

export const PasswordStrengthMeter = ({
  displayName,
  email,
  i18nLevels,
  i18nNoValue,
  i18nNoZxcvbn,
  password,
}: PasswordStrengthMeterProps) => {
  const { formatMessage } = useIntl();
  const minimumPasswordLength = getMinimumPasswordLength();

  const strengthLevelMessages = useMemo(
    () =>
      i18nLevels ||
      [
        minimumPasswordLengthMessage,
        messages.passwordStrengthLevel1,
        messages.passwordStrengthLevel2,
        messages.passwordStrengthLevel3,
        messages.passwordStrengthLevel4,
        messages.passwordStrengthLevel5,
      ].map(message => formatMessage(message, { minimumPasswordLength })),
    [formatMessage, i18nLevels, minimumPasswordLength]
  );
  const noValueMessage =
    i18nNoValue || formatMessage(minimumPasswordLengthMessage, { minimumPasswordLength });
  const noZxcvbnMessage = i18nNoZxcvbn || formatMessage(messages.passwordStrengthUnavailable);
  const { score } = usePasswordScore({ displayName, email, password });

  const getStrengthText = () => {
    if (!password) return noValueMessage;
    else if (password.length < minimumPasswordLength) return strengthLevelMessages[0];
    else if (password === email) return formatMessage(messages.passwordStrengthCantMatchEmail);
    else if (password === displayName)
      return formatMessage(messages.passwordStrengthCantMatchDisplayName);
    else if (atlassianKeywords.includes(password))
      return formatMessage(messages.passwordStrengthCantMatchAtlassianKeyword);
    else {
      return typeof score === 'number' ? strengthLevelMessages[score] : noZxcvbnMessage;
    }
  };

  return <Container title={getStrengthText()} score={score} />;
};
